import IconLoader from "@/features/Common/components/IconLoader";
import Panel from "@/features/Common/components/Panel";
import { trackUserInteraction } from "@/services/analytics-adapter";
import theme, { panelStyle } from "@/theme";
import { convertPxToRem } from "@/utils";
import { Stack, Theme, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  FeatureToggleIdentifier,
  isFeatureEnabled,
} from "@/services/feature-toggle-adapter";
import { useHealthScoreRatingColour } from "../Common/hooks/useHealthScoreRatingColour";

export interface HealthScoreChange {
  points: number;
  direction: "increase" | "decrease";
  timeFrameKey: string;
}

export interface PersonalizedPanelProps {
  healthScore?: number;
  lastUpdated?: string;
  change?: HealthScoreChange;
}

function clampScore(value: number) {
  return Math.min(100, Math.max(0, value));
}

export default function PersonalizedPanel({
  healthScore,
  lastUpdated,
  change,
}: PersonalizedPanelProps) {
  const isTrendedVitalsEnabled = isFeatureEnabled(
    FeatureToggleIdentifier.ENABLE_TRENDED_VITALS
  );
  const { t } = useTranslation();
  const navigate = useNavigate();
  const hasChange =
    change &&
    change.points !== 0 &&
    (change.direction === "increase" || change.direction === "decrease");
  const displayScore =
    healthScore !== undefined ? clampScore(healthScore) : undefined;
  const { healthScoreRatingColour } = useHealthScoreRatingColour(
    displayScore ?? 0
  );

  const handleClick = () => {
    trackUserInteraction({
      linkText: "Home screen | Get Your Personalized Health Score",
      linkIntent: "navigational",
      linkScope: "button",
    });
    navigate("/home/health-score");
  };

  if (displayScore === undefined) {
    return (
      <Stack
        sx={{
          ...panelStyle,
          cursor: "pointer",
          border: "none",
        }}
        component="button"
        role="button"
        aria-label={t("PersonalizedHealth.label")}
        spacing={1}
        onClick={handleClick}
      >
        <Stack
          flexDirection="row"
          justifyContent="space-between"
          alignItems="flex-start"
          width="100%"
          gap={2}
        >
          <Typography
            textAlign="left"
            variant={isTrendedVitalsEnabled ? "body1" : "h3"}
            fontWeight="bold"
            color="grey[700]"
          >
            {t("PersonalizedHealth.label")}
          </Typography>
          <Panel
            sx={{
              boxShadow: "none",
              height: "fit-content",
              paddingLeft: convertPxToRem(10),
              paddingRight: convertPxToRem(10),
              paddingTop: convertPxToRem(6),
              paddingBottom: convertPxToRem(6),
              backgroundColor: (theme: Theme) => theme.palette.accent1[700],
            }}
          >
            <Typography
              variant={isTrendedVitalsEnabled ? "body2" : undefined}
              fontWeight={600}
              color="white"
            >
              {t("common.free")}
            </Typography>
          </Panel>
        </Stack>
        <Stack
          style={{
            marginTop: isTrendedVitalsEnabled ? convertPxToRem(9.5) : "",
          }}
          flexDirection="row"
          alignItems="center"
          width="100%"
        >
          <Typography variant="button" fontWeight={800} color="primary.main">
            {t("PersonalizedHealth.CTA")}
          </Typography>
          <IconLoader icon="ChevronNextIcon" sx={{ color: "primary.main" }} />
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack
      sx={{
        ...panelStyle,
        cursor: "pointer",
        border: "none",
      }}
      component="button"
      role="button"
      aria-label={t("PersonalizedHealth.card.title")}
      spacing={1.5}
      onClick={handleClick}
      data-testid="personalized-panel-health-score-card"
    >
      <Stack
        flexDirection="row"
        justifyContent="space-between"
        alignItems="flex-start"
        width="100%"
      >
        <Stack spacing={0.25}>
          <Typography
            textAlign="left"
            variant="body1"
            fontWeight="bold"
            color="grey[700]"
          >
            {t("PersonalizedHealth.card.title")}
          </Typography>
          {lastUpdated && (
            <Typography
              variant="body2"
              color={theme.palette.neutral.light}
              fontWeight={600}
            >
              {t("PersonalizedHealth.card.lastUpdated", { date: lastUpdated })}
            </Typography>
          )}
        </Stack>
        <Panel
          sx={{
            boxShadow: "none",
            height: "fit-content",
            paddingLeft: convertPxToRem(10),
            paddingRight: convertPxToRem(10),
            paddingTop: convertPxToRem(6),
            paddingBottom: convertPxToRem(6),
            backgroundColor: (theme: Theme) => theme.palette.accent1[700],
          }}
        >
          <Typography variant="body2" fontWeight={600} color="white">
            {t("common.free")}
          </Typography>
        </Panel>
      </Stack>

      <Stack
        flexDirection="row"
        alignItems="center"
        justifyContent="space-between"
        width="100%"
        gap={2}
      >
        <Stack flexDirection="row" alignItems="baseline" gap={0.5}>
          <Typography
            variant="h4"
            fontWeight={700}
            fontSize={convertPxToRem(38)}
            color={healthScoreRatingColour}
            data-testid="personalized-panel-health-score-value"
          >
            {displayScore}
          </Typography>
          <Typography
            variant="body1"
            color={theme.palette.neutral[300]}
            fontSize={convertPxToRem(38)}
            fontWeight={300}
          >
            /
          </Typography>
          <Typography
            variant="body1"
            color={theme.palette.neutral[300]}
            fontSize={convertPxToRem(38)}
            fontWeight={300}
          >
            100
          </Typography>
        </Stack>
        {hasChange && change && (
          <Stack alignItems="flex-end" spacing={0}>
            <Stack flexDirection="row" alignItems="center" gap={0.5}>
              <Typography
                component="span"
                sx={(theme) => ({
                  color: theme.palette.accent3[800],
                  fontSize: convertPxToRem(14),
                  lineHeight: 1,
                })}
                aria-hidden
              >
                {change.direction === "increase" ? "▲" : "▼"}
              </Typography>
              <Typography
                variant="body2"
                fontWeight={700}
                color={theme.palette.neutral[900]}
              >
                {t("PersonalizedHealth.card.pointsChange", {
                  count: Math.abs(change.points),
                })}
              </Typography>
            </Stack>
            <Typography variant="caption" color={theme.palette.neutral[500]}>
              {t(
                change.timeFrameKey as
                  | "PersonalizedHealth.card.timeFrame.fromLastWeek"
                  | "PersonalizedHealth.card.timeFrame.fromLastMonth"
              )}
            </Typography>
          </Stack>
        )}
      </Stack>

      <Typography
        component="span"
        variant="body2"
        fontWeight={600}
        fontSize={convertPxToRem(14)}
        color="primary.main"
        sx={{ display: "flex", alignItems: "center" }}
      >
        {t("PersonalizedHealth.card.learnMore")}
        <IconLoader
          icon="ChevronNextIcon"
          sx={{ fontSize: convertPxToRem(24), color: "primary.main" }}
        />
      </Typography>
    </Stack>
  );
}
